/**
 * Security module error classes and type guards.
 *
 * @remarks
 * These error classes represent parameter mismatches detected during
 * deterministic transaction verification. They were extracted from
 * shared/error.ts to break the circular dependency between shared/
 * and modules/security/.
 *
 * @module Security Errors
 * @category BD-SDK submodules
 * @packageDocumentation
 */
import { SECURITY_MODULE_CLAIM_EXIT_QUEUE_PARAMETER_MISMATCH_ERROR_MESSAGE, SECURITY_MODULE_PARAMETER_MISMATCH_ERROR_MESSAGE, SECURITY_MODULE_STAKE_PARAMETER_MISMATCH_ERROR_MESSAGE, SECURITY_MODULE_UNKNOWN_PARAMETER_MISMATCH_ERROR_MESSAGE, SECURITY_MODULE_WITHDRAW_PARAMETER_MISMATCH_ERROR_MESSAGE, } from "./security-utils";
import { StakewiseOperationsEnum } from "./types";
/**
 * Error thrown when security module parameters do not match expectations.
 */
export class SecurityModuleParameterMismatchError extends Error {
    operation;
    details;
    decoded;
    constructor(message, operation, details, decoded) {
        super(message);
        this.name = "SecurityModuleParameterMismatchError";
        this.operation = operation;
        this.details = details;
        this.decoded = decoded;
    }
}
export class SecurityModuleStakeParameterMismatchError extends SecurityModuleParameterMismatchError {
    constructor(message, details) {
        super(message, StakewiseOperationsEnum.STAKE, details);
    }
}
export class SecurityModuleWithdrawParameterMismatchError extends SecurityModuleParameterMismatchError {
    constructor(message, details) {
        super(message, StakewiseOperationsEnum.WITHDRAW, details);
    }
}
export class SecurityModuleClaimExitQueueParameterMismatchError extends SecurityModuleParameterMismatchError {
    constructor(message, details) {
        super(message, StakewiseOperationsEnum.CLAIM_EXIT_QUEUE, details);
    }
}
/**
 * Type guard to check if an error is a SecurityModuleParameterMismatchError.
 *
 * @param error - The error to check
 * @returns True if the error is a SecurityModuleParameterMismatchError
 *
 * @example
 * ```typescript
 * try {
 *   await sdk.staking.buildStakeTx(request);
 * } catch (error) {
 *   if (isSecurityModuleParameterMismatch(error)) {
 *     console.error("Security check failed:", error.details);
 *   }
 * }
 * ```
 */
export function isSecurityModuleParameterMismatch(error) {
    return error instanceof SecurityModuleParameterMismatchError;
}
/**
 * Factory function to create operation-specific security module errors.
 *
 * Uses the predefined error messages for each operation type as defaults.
 * Provides a convenient way to create the appropriate error subclass based on operation.
 *
 * @param operation - The security operation type
 * @param details - Optional error details
 * @param message - Optional custom message (defaults to operation-specific message)
 * @returns The appropriate SecurityModuleParameterMismatchError subclass
 *
 * @example
 * ```typescript
 * // Create stake-specific error with default message
 * throw throwSecurityModuleMismatchError("stake", { value: "100" });
 *
 * // Create error with custom message
 * throw throwSecurityModuleMismatchError("withdraw", { amount: "50" }, "Custom error");
 * ```
 */
export function throwSecurityModuleMismatchError(operation = StakewiseOperationsEnum.GENERIC, details, message) {
    switch (operation) {
        case "stake":
            return new SecurityModuleStakeParameterMismatchError(message ?? SECURITY_MODULE_STAKE_PARAMETER_MISMATCH_ERROR_MESSAGE, details);
        case "withdraw":
            return new SecurityModuleWithdrawParameterMismatchError(message ?? SECURITY_MODULE_WITHDRAW_PARAMETER_MISMATCH_ERROR_MESSAGE, details);
        case "claimExitQueue":
            return new SecurityModuleClaimExitQueueParameterMismatchError(message ?? SECURITY_MODULE_CLAIM_EXIT_QUEUE_PARAMETER_MISMATCH_ERROR_MESSAGE, details);
        case "unknown":
            return new SecurityModuleParameterMismatchError(message ?? SECURITY_MODULE_UNKNOWN_PARAMETER_MISMATCH_ERROR_MESSAGE, StakewiseOperationsEnum.UNKNOWN, details);
        default:
            return new SecurityModuleParameterMismatchError(message ?? SECURITY_MODULE_PARAMETER_MISMATCH_ERROR_MESSAGE, operation, details);
    }
}
//# sourceMappingURL=data:application/json;base64,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